'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useTranslations, useLocale } from 'next-intl'

const CONSENT_KEY = 'sb_cookie_consent'

export type ConsentState = 'accepted' | 'declined' | null

function grantConsent() {
  if (typeof window === 'undefined') return
  if (typeof (window as any).gtag !== 'function') {
    // gtag/js hasn't loaded yet — queue it so it fires once the library arrives
    ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any).dataLayer.push(['consent', 'update', { analytics_storage: 'granted' }])
    return
  }
  ;(window as any).gtag('consent', 'update', { analytics_storage: 'granted' })
}

export function CookieBanner() {
  const t = useTranslations('cookieBanner')
  const locale = useLocale()
  const [consent, setConsent] = useState<ConsentState | 'loading'>('loading')

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentState | null
    setConsent(stored)
    if (stored === 'accepted') {
      grantConsent()
    }
  }, [])

  function accept() {
    localStorage.setItem(CONSENT_KEY, 'accepted')
    setConsent('accepted')
    grantConsent()
  }

  function decline() {
    localStorage.setItem(CONSENT_KEY, 'declined')
    setConsent('declined')
  }

  // Don't render until we've checked localStorage, and hide if already decided
  if (consent !== null) return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('ariaLabel')}
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-in slide-in-from-bottom-5 fade-in duration-500"
    >
      <div className="max-w-4xl mx-auto bg-white/80 dark:bg-black/80 backdrop-blur-xl border border-neutral-200/50 dark:border-white/10 rounded-2xl shadow-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-base font-medium text-foreground mb-1 font-mono uppercase tracking-wider">{t('title')}</p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
            {t('description')}{' '}
            <Link
              href={`/${locale}/cookie-policy`}
              className="text-[var(--neon-orange)] hover:underline underline-offset-2 transition-colors"
            >
              {t('learnMore')}
            </Link>
          </p>
        </div>
        <div className="flex gap-3 shrink-0 mt-2 md:mt-0">
          <button
            onClick={decline}
            className="px-5 py-2.5 text-sm font-medium rounded-lg border border-neutral-300 dark:border-neutral-700 bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-800 text-foreground transition-colors"
          >
            {t('decline')}
          </button>
          <button
            onClick={accept}
            className="px-5 py-2.5 text-sm font-medium rounded-lg bg-[var(--neon-orange)] text-white hover:bg-[var(--neon-orange)]/90 transition-colors shadow-lg shadow-orange-500/20"
          >
            {t('accept')}
          </button>
        </div>
      </div>
    </div>
  )
}
